import { UploadProgress } from '../../flightUpload/uploadProgress/UploadProgress';
import { GcpRequestData, useUploadStore } from '../../../../../../store/uploadStore';
import { GcpRequestFileType } from '../gcpRequestFileType';
import { ProjectData } from '../../../types';
import { useHandleRemoveGcpFiles } from './useHandleRemoveGcpFiles';
import { getGcpProgressUiElements } from './getGcpProgressUiElements';
import { GcpServiceHoursConfirmation, GcpServiceHoursRow, shouldConfirmGcpServiceHours } from '../GcpServiceHoursRow';
import { GcpMeasurementErrorItem } from './GcpMeasurementErrorItem';
import { getGcpRequestMeasurementDates } from '../gcpRequestUploadFiles/useHandleGcpFileUpload';
import { useAppStore } from '../../../../../../store/appStore';

interface Props extends ProjectData {
  gcpRequest: GcpRequestData;
  type: GcpRequestFileType;
}

export const GcpRequestUploadProgress = ({ customer, site, gcpRequest, type }: Props) => {
  const isMeasurement = type === GcpRequestFileType.Measurement;
  const { setGcpRequest } = useUploadStore();
  const { pilot } = useAppStore();

  const files = isMeasurement ? gcpRequest.measurements : gcpRequest.photos;
  const uploads = isMeasurement ? gcpRequest.measurementUploads : gcpRequest.photoUploads;

  // The Emlid time-marks validation only applies to pilots with a record from GET /api/v1/pilot —
  // pilots without one aren't held to that file format, so treat their uploads as successful.
  const erroredUploads = isMeasurement && pilot?.id ? uploads.filter((u) => u.isDone && !!u.parseResult?.error) : [];
  const erroredUrlSet = new Set(erroredUploads.map((u) => u.uploadedUrl).filter((url): url is string => !!url));
  const successfulUploads = erroredUploads.length ? uploads.filter((u) => !erroredUploads.includes(u)) : uploads;
  const successfulFiles = erroredUploads.length ? files.filter((f) => !erroredUrlSet.has(f)) : files;

  const handleRemoveClick = useHandleRemoveGcpFiles(customer, site, gcpRequest, type, files, uploads);

  const hasSuccessfulContent = successfulFiles.length > 0 || successfulUploads.some((u) => !u.isDone);

  if (!hasSuccessfulContent && !erroredUploads.length) {
    return null;
  }

  const { icon, title, note, removeIcon, progress, isDone } = getGcpProgressUiElements(
    type,
    successfulFiles,
    successfulUploads,
  );

  const measurementDates = isMeasurement ? getGcpRequestMeasurementDates(gcpRequest) : [];
  const hasMixedMeasurementDates = measurementDates.length > 1;

  const handleServiceHoursChange = (hours: number | undefined) => {
    gcpRequest.gcpServiceLaborTimeInHours = hours;
    setGcpRequest(customer.id, site.id, gcpRequest);
  };

  const handleServiceHoursConfirmedChange = (confirmed: boolean) => {
    gcpRequest.meta.areServiceHoursConfirmed = confirmed;
    setGcpRequest(customer.id, site.id, gcpRequest);
  };

  const serviceHoursFooter =
    isMeasurement && pilot?.id && isDone && (gcpRequest.meta.isEditMode || gcpRequest.gcpServiceLaborTimeInHours) ? (
      <>
        <GcpServiceHoursRow
          hours={gcpRequest.gcpServiceLaborTimeInHours}
          defaultHours={gcpRequest.autoGcpServiceLaborTimeInHours ?? gcpRequest.meta.prevGcpServiceLaborTimeInHours}
          gcpCount={gcpRequest.gcpCount}
          serviceDate={hasMixedMeasurementDates ? measurementDates[measurementDates.length - 1] : undefined}
          isEditMode={gcpRequest.meta.isEditMode}
          onChange={handleServiceHoursChange}
        />
        {shouldConfirmGcpServiceHours(gcpRequest) && (
          <GcpServiceHoursConfirmation
            measurementDates={measurementDates}
            confirmed={!!gcpRequest.meta.areServiceHoursConfirmed}
            onConfirmedChange={handleServiceHoursConfirmedChange}
          />
        )}
      </>
    ) : undefined;

  return (
    <>
      {hasSuccessfulContent && (
        <UploadProgress
          icon={icon}
          title={title}
          note={note}
          removeIcon={removeIcon}
          onRemoveClick={handleRemoveClick}
          progress={gcpRequest.meta.isEditMode ? progress : undefined}
          isDone={isDone}
          disableCancel={!gcpRequest.meta.isEditMode}
          footer={serviceHoursFooter}
        />
      )}
      {erroredUploads.map((upload) => (
        <GcpMeasurementErrorItem
          key={`${upload.file.name}-${upload.file.size}`}
          upload={upload}
          customer={customer}
          site={site}
          gcpRequest={gcpRequest}
        />
      ))}
    </>
  );
};
